import React, { useState } from 'react';
import { Icon, Button, Input, Textarea, Modal, Avatar, Badge } from '../components/UI';
import { useApp } from '../context/AppContext';

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

export default function AdminEmailPage() {
  const { user, data, sendEmail } = useApp();
  const [view, setView] = useState('compose'); // compose | sent | detail
  const [selected, setSelected] = useState(null);
  const [recipientType, setRecipientType] = useState('all'); // all | programme | individual
  const [selectedProg, setSelectedProg] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const students = data.students.filter(s => s.role === 'student');

  const getRecipients = () => {
    if (recipientType === 'all') return students.map(s => s.id);
    if (recipientType === 'programme') {
      const prog = data.programmes.find(p => p.id === selectedProg);
      if (!prog) return [];
      return students.filter(s => s.programmes.includes(selectedProg)).map(s => s.id);
    }
    return selectedStudents;
  };

  const recipientCount = getRecipients().length;

  const handleSend = async () => {
    if (!subject.trim()) { setError('Please add a subject.'); return; }
    if (!body.trim()) { setError('Please write a message.'); return; }
    const recipients = getRecipients();
    if (recipients.length === 0) { setError('No recipients selected.'); return; }

    try {
      await sendEmail({
        toAll: recipientType === 'all',
        toProgramme: recipientType === 'programme' ? selectedProg : null,
        toStudents: recipientType === 'individual' ? selectedStudents : [],
        subject: subject.trim(),
        body: body.trim(),
      });
      setSent(true);
      setError('');
      setTimeout(() => {
        setSent(false);
        setSubject('');
        setBody('');
        setSelectedStudents([]);
        setSelectedProg('');
        setRecipientType('all');
      }, 2500);
    } catch {
      setError('Failed to send. Please try again.');
    }
  };

  const sentEmails = data.emails
    .filter(e => e.from === user.id)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const getRecipientLabel = (email) => {
    if (email.toAll) return `All students (${email.to.length})`;
    if (email.toProgramme) {
      const prog = data.programmes.find(p => p.id === email.toProgramme);
      return `${prog?.name || 'Programme'} (${email.to.length} student${email.to.length !== 1 ? 's' : ''})`;
    }
    const names = email.to.map(id => {
      const s = data.students.find(st => st.id === id);
      return s ? s.name.split(' ')[0] : id;
    });
    return names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2} more` : names.join(', ');
  };

  const openSentDetail = (email) => { setSelected(email); setView('detail'); };

  const toggleStudent = (id) => {
    setSelectedStudents(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  return (
    <div className="page-enter" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 30, fontWeight: 800, color: 'var(--dark)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>Email</h1>
          <p style={{ fontSize: 14, color: 'var(--dark-60)', margin: 0 }}>Communicate directly with your students</p>
        </div>
        <div style={{ display: 'flex', gap: 6, background: 'var(--dark-05)', borderRadius: 'var(--radius-md)', padding: 4 }}>
          {[{ id: 'compose', icon: 'edit', label: 'Compose' }, { id: 'sent', icon: 'send', label: `Sent (${sentEmails.length})` }].map(tab => (
            <button key={tab.id} onClick={() => { setView(tab.id); setSelected(null); }}
              style={{ fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 600, padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: view === tab.id ? 'var(--white)' : 'transparent', color: view === tab.id ? 'var(--dark)' : 'var(--dark-60)', display: 'flex', alignItems: 'center', gap: 6, boxShadow: view === tab.id ? '0 1px 4px rgba(45,48,53,0.10)' : 'none', transition: 'all 0.15s' }}>
              <Icon name={tab.icon} size={14} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* COMPOSE */}
      {view === 'compose' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>
          <div style={{ background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-xl)', padding: 32 }}>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--dark)', margin: '0 0 24px' }}>New message</h2>

            {/* Recipient type */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 600, color: 'var(--dark-80)', marginBottom: 10 }}>Send to</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                {[{ id: 'all', label: 'All students' }, { id: 'programme', label: 'By programme' }, { id: 'individual', label: 'Individual' }].map(opt => (
                  <button key={opt.id} onClick={() => { setRecipientType(opt.id); setSelectedStudents([]); setSelectedProg(''); }}
                    style={{ fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 600, padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${recipientType === opt.id ? 'var(--orange)' : 'var(--dark-20)'}`, cursor: 'pointer', background: recipientType === opt.id ? 'var(--orange-soft)' : 'transparent', color: recipientType === opt.id ? 'var(--orange)' : 'var(--dark-60)', transition: 'all 0.15s' }}>
                    {opt.label}
                  </button>
                ))}
              </div>

              {recipientType === 'programme' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
                  {data.programmes.map(prog => {
                    const count = students.filter(s => s.programmes.includes(prog.id)).length;
                    const selected = selectedProg === prog.id;
                    return (
                      <div key={prog.id} onClick={() => setSelectedProg(selected ? '' : prog.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', border: `1.5px solid ${selected ? prog.color : 'var(--dark-20)'}`, borderRadius: 'var(--radius-md)', cursor: 'pointer', background: selected ? prog.color + '0C' : 'transparent', transition: 'all 0.15s' }}>
                        <Icon name={prog.icon} size={16} color={selected ? prog.color : 'var(--dark-40)'} />
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 700, color: selected ? prog.color : 'var(--dark)', margin: 0, fontFamily: 'var(--font-heading)' }}>{prog.name}</p>
                          <p style={{ fontSize: 11, color: 'var(--dark-40)', margin: 0 }}>{count} student{count !== 1 ? 's' : ''}</p>
                        </div>
                        {selected && <Icon name="check" size={14} color={prog.color} style={{ marginLeft: 'auto' }} />}
                      </div>
                    );
                  })}
                </div>
              )}

              {recipientType === 'individual' && (
                <div style={{ border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-md)', overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
                  {students.map((s, i) => {
                    const checked = selectedStudents.includes(s.id);
                    return (
                      <div key={s.id} onClick={() => toggleStudent(s.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderBottom: i < students.length - 1 ? '1px solid var(--dark-10)' : 'none', cursor: 'pointer', background: checked ? 'var(--orange-soft)' : 'transparent', transition: 'background 0.15s' }}>
                        <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${checked ? 'var(--orange)' : 'var(--dark-20)'}`, background: checked ? 'var(--orange)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {checked && <Icon name="check" size={11} color="white" />}
                        </div>
                        <Avatar name={s.name} size={30} />
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--dark)', margin: 0, fontFamily: 'var(--font-heading)' }}>{s.name}</p>
                          <p style={{ fontSize: 11, color: 'var(--dark-60)', margin: 0 }}>{s.email}</p>
                        </div>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {s.programmes.slice(0, 2).map(pid => {
                            const prog = data.programmes.find(p => p.id === pid);
                            return prog ? <Badge key={pid} bg={prog.color + '18'} color={prog.color}>{prog.name}</Badge> : null;
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <Input label="Subject" value={subject} onChange={setSubject} required placeholder="Message subject…" />
            <Textarea label="Message" value={body} onChange={setBody} rows={8} required placeholder="Write your message here…" />

            {error && (
              <div style={{ background: 'var(--danger-soft)', border: '1px solid rgba(214,69,69,0.2)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="alert" size={15} color="var(--danger)" />
                <p style={{ color: 'var(--danger)', fontSize: 13, margin: 0 }}>{error}</p>
              </div>
            )}

            {sent ? (
              <div style={{ background: 'var(--success-soft)', borderRadius: 'var(--radius-md)', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon name="check" size={18} color="var(--success)" />
                <p style={{ color: 'var(--success)', fontWeight: 700, fontSize: 14, margin: 0, fontFamily: 'var(--font-heading)' }}>Message sent to {recipientCount} student{recipientCount !== 1 ? 's' : ''}!</p>
              </div>
            ) : (
              <Button onClick={handleSend} size="lg">
                <Icon name="send" size={15} color="white" /> Send to {recipientCount} student{recipientCount !== 1 ? 's' : ''}
              </Button>
            )}
          </div>

          {/* Preview panel */}
          <div style={{ background: 'var(--dark-05)', borderRadius: 'var(--radius-xl)', padding: 24 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--dark-60)', letterSpacing: '0.1em', textTransform: 'uppercase', margin: '0 0 16px', fontFamily: 'var(--font-heading)' }}>Recipient preview</p>
            {getRecipients().length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--dark-40)', lineHeight: 1.6 }}>Select recipients above to see who will receive this message.</p>
            ) : (
              <div>
                <p style={{ fontSize: 13, color: 'var(--dark-60)', margin: '0 0 12px' }}><strong style={{ color: 'var(--dark)' }}>{getRecipients().length}</strong> recipient{getRecipients().length !== 1 ? 's' : ''}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {getRecipients().slice(0, 6).map(id => {
                    const s = data.students.find(st => st.id === id);
                    return s ? (
                      <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', background: 'var(--white)', borderRadius: 8 }}>
                        <Avatar name={s.name} size={28} />
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--dark)', margin: 0, fontFamily: 'var(--font-heading)' }}>{s.name}</p>
                          <p style={{ fontSize: 11, color: 'var(--dark-60)', margin: 0 }}>{s.email}</p>
                        </div>
                      </div>
                    ) : null;
                  })}
                  {getRecipients().length > 6 && (
                    <p style={{ fontSize: 12, color: 'var(--dark-40)', textAlign: 'center', margin: 0, padding: '4px 0' }}>+{getRecipients().length - 6} more</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SENT */}
      {view === 'sent' && !selected && (
        sentEmails.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', background: 'var(--dark-05)', borderRadius: 'var(--radius-xl)' }}>
            <Icon name="send" size={40} color="var(--dark-20)" />
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: 'var(--dark-60)', margin: '14px 0 6px' }}>No sent messages</h3>
            <p style={{ fontSize: 14, color: 'var(--dark-40)', margin: 0 }}>Messages you send will appear here.</p>
          </div>
        ) : (
          <div style={{ background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
            {sentEmails.map((email, i) => {
              const openCount = Object.values(email.read).filter(Boolean).length;
              const totalCount = email.to.length;
              return (
                <div key={email.id} onClick={() => openSentDetail(email)}
                  style={{ padding: '18px 24px', borderBottom: i < sentEmails.length - 1 ? '1px solid var(--dark-10)' : 'none', cursor: 'pointer', transition: 'background 0.1s', display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--dark-05)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <p style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: 'var(--dark)', margin: 0 }}>{email.subject}</p>
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--dark-60)', margin: '0 0 4px' }}>To: {getRecipientLabel(email)}</p>
                    <p style={{ fontSize: 12, color: 'var(--dark-40)', margin: 0 }}>{formatDate(email.date)}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 12, color: openCount > 0 ? 'var(--success)' : 'var(--dark-40)', fontWeight: 600, margin: '0 0 4px', fontFamily: 'var(--font-heading)' }}>
                      {openCount}/{totalCount} opened
                    </p>
                    <div style={{ height: 4, width: 80, background: 'var(--dark-10)', borderRadius: 99, overflow: 'hidden', marginLeft: 'auto' }}>
                      <div style={{ height: '100%', width: `${totalCount > 0 ? (openCount / totalCount) * 100 : 0}%`, background: 'var(--success)', borderRadius: 99 }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* SENT DETAIL */}
      {view === 'detail' && selected && (
        <div>
          <button onClick={() => { setView('sent'); setSelected(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'var(--dark-60)', fontFamily: 'var(--font-heading)', fontWeight: 600, marginBottom: 20, padding: 0 }}>
            <Icon name="chevronRight" size={14} style={{ transform: 'rotate(180deg)' }} /> Back to sent
          </button>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, alignItems: 'start' }}>
            <div style={{ background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-xl)', padding: 32 }}>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 800, color: 'var(--dark)', margin: '0 0 8px', letterSpacing: '-0.01em' }}>{selected.subject}</h2>
              <p style={{ fontSize: 13, color: 'var(--dark-60)', margin: '0 0 24px' }}>{formatDate(selected.date)} · To: {getRecipientLabel(selected)}</p>
              <div style={{ height: 1, background: 'var(--dark-10)', marginBottom: 24 }} />
              <div style={{ fontSize: 15, color: 'var(--dark)', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{selected.body}</div>
            </div>

            <div style={{ background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-xl)', padding: 22 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--dark-60)', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 16px', fontFamily: 'var(--font-heading)' }}>Read receipts</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {selected.to.map(id => {
                  const s = data.students.find(st => st.id === id);
                  const isRead = selected.read[id];
                  return s ? (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={s.name} size={30} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--dark)', margin: 0, fontFamily: 'var(--font-heading)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</p>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: isRead ? 'var(--success)' : 'var(--dark-40)', background: isRead ? 'var(--success-soft)' : 'var(--dark-05)', padding: '2px 8px', borderRadius: 99, fontFamily: 'var(--font-heading)', flexShrink: 0 }}>
                        {isRead ? 'Read' : 'Unread'}
                      </span>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
